/**
 * The three primitives every page in this app builds its forms out of.
 *
 * They were declared once per page and had begun to diverge in name before
 * they diverged in behaviour: `Field` on Cards and Points, the same component
 * under the name `Text` on Offers. Three identical labelled inputs, two
 * identical labelled selects, and two metric tiles where one was a strict
 * superset of the other.
 *
 * Kept deliberately small. These wrap a label around a control and nothing
 * else; anything that needs validation, error text or layout opinions belongs
 * at the call site, where the rules it enforces are visible.
 */

/** A labelled text input. `type` covers the date and number variants. */
export function Field({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-neutral-400">
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-10 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
      />
    </label>
  );
}

/**
 * An option whose stored value differs from what it is called.
 *
 * The filter selects on Credits and Offers pick a display string and send that
 * same string back, so a bare `string[]` is right for them. A cadence does
 * not work that way: the database holds `semi-annual` and the live page shows
 * "Semi-Annual", and a select that sent its label would be refused by
 * `_reject_unknown_cadence`.
 */
export interface Option {
  value: string;
  label: string;
}

function normalize(o: string | Option): Option {
  return typeof o === "string" ? { value: o, label: o } : o;
}

/** A labelled select. Options are plain strings, or value/label pairs. */
export function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: (string | Option)[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-neutral-400">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-10 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
      >
        {options.map(normalize).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
